import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";
import {
  LogOut, Users, ClipboardList, Award, Clock, FileText,
  UserPlus, Download, Eye, Monitor, Briefcase, Code, X, Search,
  ShieldCheck, AlertTriangle, MousePointer, ArrowLeftRight,
  BarChart3, TrendingUp, Target, CheckCircle, XCircle, Activity, Camera, Pencil
} from "lucide-react";
import { generateExamReport, generateCandidateReport } from "../utils/pdfGenerator";
import BulkCandidateImport from "./BulkCandidateImport";
import QuestionPreviewDialog from "./QuestionPreviewDialog";
import { Upload } from "lucide-react";

const BRAND = "edSolutions360";

const OrganiserDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  // State
  const [analytics, setAnalytics] = useState(null);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExam, setSelectedExam] = useState(null);
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [topicData, setTopicData] = useState([]);
  const [proctoringLogs, setProctoringLogs] = useState([]);
  const [proctoringLogsLoading, setProctoringLogsLoading] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignEmails, setAssignEmails] = useState("");
  const [assignExam, setAssignExam] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateProcLogs, setCandidateProcLogs] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [bulkImportExam, setBulkImportExam] = useState(null);
  const [inviteCandidateExam, setInviteCandidateExam] = useState(null);
  const [inviteForm, setInviteForm] = useState({ full_name: '', email: '', application_number: '', mobile: '' });
  const [invitePhoto, setInvitePhoto] = useState(null);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [candidateVerifications, setCandidateVerifications] = useState([]);
  const [verifyingCandidate, setVerifyingCandidate] = useState(null);
  const [verificationExamId, setVerificationExamId] = useState(null);
  const [editCandidate, setEditCandidate] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', application_number: '', mobile: '' });
  const [editPhoto, setEditPhoto] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [uploadingPhotoFor, setUploadingPhotoFor] = useState(null);
  const [previewExam, setPreviewExam] = useState(null);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [analyticsRes, examsRes] = await Promise.all([
        axios.get(`${API}/organiser/analytics`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/organiser/exams`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAnalytics(analyticsRes.data);
      setExams(examsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const selectExam = async (exam) => {
    setSelectedExam(exam);
    setActiveTab("results");
    setResultsLoading(true);
    setProctoringLogsLoading(true);
    try {
      const [resultsRes, topicRes, procRes] = await Promise.all([
        axios.get(`${API}/organiser/exams/${exam.id}/results`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/organiser/exams/${exam.id}/topic-performance`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/organiser/exams/${exam.id}/proctoring-logs`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setResults(resultsRes.data);
      setTopicData(topicRes.data);
      setProctoringLogs(procRes.data);
    } catch (error) {
      toast.error("Failed to fetch exam data");
    } finally {
      setResultsLoading(false);
      setProctoringLogsLoading(false);
    }
  };

  const handleAssignCandidates = async () => {
    if (!assignExam || !assignEmails.trim()) return;
    try {
      const emails = assignEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e);
      await axios.post(`${API}/organiser/exams/${assignExam.id}/assign-candidates`, emails, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invited ${emails.length} candidate(s)`);
      setShowAssignDialog(false);
      setAssignEmails("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to invite candidates");
    }
  };

  const handleInviteCandidate = async () => {
    if (!inviteCandidateExam || !inviteForm.email.trim() || !inviteForm.full_name.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setInviteSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('full_name', inviteForm.full_name);
      formData.append('email', inviteForm.email);
      formData.append('application_number', inviteForm.application_number || '');
      formData.append('mobile', inviteForm.mobile || '');
      if (invitePhoto) formData.append('photo', invitePhoto);
      await axios.post(`${API}/organiser/exams/${inviteCandidateExam.id}/invite-candidate`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Candidate ${inviteForm.full_name} invited successfully`);
      setInviteCandidateExam(null);
      setInviteForm({ full_name: '', email: '', application_number: '', mobile: '' });
      setInvitePhoto(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to invite candidate");
    } finally {
      setInviteSubmitting(false);
    }
  };

  const fetchCandidateVerifications = async (examId) => {
    try {
      const { data } = await axios.get(`${API}/organiser/exams/${examId}/candidates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidateVerifications(data);
      setVerificationExamId(examId);
    } catch (error) {
      toast.error("Failed to load candidate verifications");
    }
  };

  const handleManualVerify = async (examId, candidateId) => {
    setVerifyingCandidate(candidateId);
    try {
      await axios.post(`${API}/organiser/exams/${examId}/manual-verify/${candidateId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Candidate manually verified");
      fetchCandidateVerifications(examId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to verify");
    } finally {
      setVerifyingCandidate(null);
    }
  };

  const openEditCandidate = (candidate) => {
    setEditCandidate(candidate);
    setEditForm({
      full_name: candidate.full_name || '',
      application_number: candidate.application_number || '',
      mobile: candidate.mobile || ''
    });
    setEditPhoto(null);
  };

  const handleUpdateCandidate = async () => {
    if (!editCandidate || !verificationExamId) return;
    setEditSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('full_name', editForm.full_name);
      formData.append('application_number', editForm.application_number);
      formData.append('mobile', editForm.mobile || '');
      if (editPhoto) formData.append('photo', editPhoto);
      await axios.put(`${API}/organiser/exams/${verificationExamId}/candidates/${editCandidate.id}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Candidate profile updated");
      setEditCandidate(null);
      fetchCandidateVerifications(verificationExamId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update candidate");
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleQuickPhotoUpload = async (candidateId, file) => {
    if (!file || !verificationExamId) return;
    setUploadingPhotoFor(candidateId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/organiser/exams/${verificationExamId}/candidates/${candidateId}/upload-photo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Photo uploaded successfully");
      fetchCandidateVerifications(verificationExamId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhotoFor(null);
    }
  };


  const handleRemoveCandidate = async (examId, email) => {
    if (!window.confirm(`Remove ${email}?`)) return;
    try {
      await axios.delete(`${API}/organiser/exams/${examId}/remove-candidate?email=${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Candidate removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const viewCandidateDetail = async (examId, attemptId) => {
    setDetailLoading(true);
    setShowDetailDialog(true);
    setCandidateProcLogs([]);
    try {
      const { data } = await axios.get(`${API}/organiser/exams/${examId}/results/${attemptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCandidate(data);
      // Filter proctoring logs for this candidate
      const candidateLogs = proctoringLogs.filter(l => l.candidate_id === data.candidate_id);
      setCandidateProcLogs(candidateLogs);
    } catch (error) {
      toast.error("Failed to load details");
      setShowDetailDialog(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadExamPdf = async (exam) => {
    setGeneratingPdf(exam.id);
    try {
      const [resultsRes, topicRes] = await Promise.all([
        axios.get(`${API}/organiser/exams/${exam.id}/results`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/organiser/exams/${exam.id}/topic-performance`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      generateExamReport(exam, resultsRes.data, topicRes.data);
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to generate PDF");
    } finally {
      setGeneratingPdf(null);
    }
  };

  const downloadCandidatePdf = async (attempt) => {
    if (!selectedExam) return;
    try {
      const { data } = await axios.get(`${API}/organiser/exams/${selectedExam.id}/results/${attempt.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      generateCandidateReport(data, selectedExam);
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to generate PDF");
    }
  };

  const handleLogout = () => { logout(); navigate("/auth"); };

  const getRoleLabel = (r) => r === "BA" ? "Business Analyst" : r === "Frontend" ? "Frontend Developer" : r === "React Frontend" ? "React Frontend" : "MERN Developer";
  const getRoleIcon = (r) => r === "BA" ? Briefcase : r === "Frontend" || r === "React Frontend" ? Monitor : Code;

  const getRecBadge = (rec) => {
    const m = {
      "Strongly Recommended": "bg-emerald-50 text-emerald-700 border border-emerald-200",
      "Recommended": "bg-blue-50 text-blue-700 border border-blue-200",
      "Consider": "bg-amber-50 text-amber-700 border border-amber-200",
      "Not Recommended": "bg-red-50 text-red-700 border border-red-200"
    };
    return m[rec] || "bg-zinc-100 text-zinc-700 border border-zinc-200";
  };

  const formatIST = (ts) => {
    if (!ts) return "N/A";
    const d = new Date(ts);
    return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
  };

  const getEventIcon = (type) => {
    switch (type) {
      case "tab_switch": return <ArrowLeftRight className="w-4 h-4 text-red-600" />;
      case "right_click": return <MousePointer className="w-4 h-4 text-red-600" />;
      case "copy_attempt": return <FileText className="w-4 h-4 text-red-600" />;
      case "fullscreen_exit": return <Monitor className="w-4 h-4 text-red-600" />;
      default: return <AlertTriangle className="w-4 h-4 text-red-600" />;
    }
  };

  const getEventLabel = (type) => {
    const labels = {
      tab_switch: "Tab Switch",
      right_click: "Right Click",
      copy_attempt: "Copy Attempt",
      fullscreen_exit: "Fullscreen Exit"
    };
    return labels[type] || type;
  };

  const filteredResults = results.filter(r =>
    !searchQuery ||
    r.candidate_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.candidate_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const CHART_COLORS = ["#18181b", "#3f3f46", "#71717a", "#a1a1aa", "#d4d4d8"];
  const REC_COLORS = { "Strongly Recommended": "#059669", "Recommended": "#2563eb", "Consider": "#d97706", "Not Recommended": "#dc2626" };

  const recDistribution = useMemo(() => {
    const counts = {};
    results.forEach(r => { counts[r.recommendation] = (counts[r.recommendation] || 0) + 1; });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [results]);

  return (
    <div className="flex min-h-screen bg-zinc-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="organiser-dashboard">
      {/* Sidebar */}
      <aside className="w-64 border-r border-zinc-200 bg-zinc-50 h-screen fixed left-0 top-0 hidden md:flex flex-col z-40">
        <div className="p-6 border-b border-zinc-200">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-sm bg-zinc-900 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="leading-tight">
              <p className="text-sm font-bold text-zinc-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{BRAND}</p>
              <p className="text-[10px] tracking-[0.15em] uppercase text-zinc-400 font-medium">Examination Portal</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {[
            { id: "overview", icon: BarChart3, label: "Overview" },
            { id: "exams", icon: ClipboardList, label: "Examinations" },
            { id: "results", icon: Award, label: "Results" },
            { id: "proctoring", icon: AlertTriangle, label: "Proctoring" },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                activeTab === item.id
                  ? "bg-zinc-900 text-white font-medium"
                  : "text-zinc-600 hover:bg-zinc-100"
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-zinc-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-sm bg-zinc-200 flex items-center justify-center">
              <Users className="w-4 h-4 text-zinc-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-zinc-900 truncate">{user?.full_name}</p>
              <p className="text-[11px] text-zinc-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full rounded-sm text-xs h-8 border-zinc-200" onClick={handleLogout} data-testid="organiser-logout-btn">
            <LogOut className="w-3 h-3 mr-1.5" />Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-zinc-200 sticky top-0 z-30 h-14 flex items-center justify-between px-6">
          <div>
            <h1 className="text-lg font-semibold text-zinc-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {activeTab === "overview" && "Dashboard Overview"}
              {activeTab === "exams" && "Examinations"}
              {activeTab === "results" && (selectedExam ? `Results: ${selectedExam.name}` : "Results")}
              {activeTab === "proctoring" && "Proctoring & Malpractice"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {/* Mobile brand */}
            <div className="flex md:hidden items-center gap-2 mr-4">
              <ShieldCheck className="w-5 h-5 text-zinc-900" />
              <span className="text-sm font-bold text-zinc-900">{BRAND}</span>
            </div>
          </div>
        </header>

        <div className="p-6 max-w-[1400px] mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-24"><div className="spinner" /></div>
          ) : (
            <>
              {/* ===== OVERVIEW TAB ===== */}
              {activeTab === "overview" && (
                <div className="space-y-6">
                  {/* Metrics Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="metrics-row">
                    {[
                      { label: "Total Exams", value: analytics?.total_exams || 0, icon: ClipboardList, color: "bg-zinc-900 text-white" },
                      { label: "Active Candidates", value: analytics?.total_candidates || 0, icon: Users, color: "bg-blue-600 text-white" },
                      { label: "Avg Score", value: `${analytics?.avg_score || 0}%`, icon: TrendingUp, color: "bg-emerald-600 text-white" },
                      { label: "Malpractice Flags", value: analytics?.total_malpractice_flags || 0, icon: AlertTriangle, color: "bg-red-600 text-white" },
                    ].map((m, i) => (
                      <div key={i} className="bg-white border border-zinc-200 rounded-sm p-5" data-testid={`metric-${m.label.toLowerCase().replace(/\s/g, '-')}`}>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-400">{m.label}</span>
                          <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${m.color}`}>
                            <m.icon className="w-4 h-4" />
                          </div>
                        </div>
                        <p className="text-3xl font-bold text-zinc-900 tracking-tight">{m.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Exam Overview Cards */}
                  <div>
                    <h3 className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-400 mb-4">Your Examinations</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {exams.map(exam => {
                        const RoleIcon = getRoleIcon(exam.role);
                        const progress = exam.total_candidates > 0 ? Math.round((exam.completed_count / exam.total_candidates) * 100) : 0;
                        return (
                          <div
                            key={exam.id}
                            className="bg-white border border-zinc-200 rounded-sm p-5 cursor-pointer hover:border-zinc-400 transition-colors"
                            onClick={() => selectExam(exam)}
                            data-testid={`exam-card-${exam.id}`}
                          >
                            <div className="flex items-center justify-between mb-4">
                              <Badge variant="outline" className="rounded-sm text-xs border-zinc-300 text-zinc-600">{getRoleLabel(exam.role)}</Badge>
                              <RoleIcon className="w-5 h-5 text-zinc-400" />
                            </div>
                            <h4 className="font-semibold text-zinc-900 mb-3 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{exam.name}</h4>
                            <div className="space-y-2 text-sm text-zinc-600">
                              <div className="flex justify-between"><span>Candidates</span><span className="font-medium text-zinc-900">{exam.total_candidates}</span></div>
                              <div className="flex justify-between"><span>Completed</span><span className="font-medium text-emerald-600">{exam.completed_count}</span></div>
                              <div className="flex justify-between"><span>Duration</span><span className="font-medium text-zinc-900">{exam.duration_minutes}m</span></div>
                              <div className="flex justify-between"><span>Face Verify</span>
                                <span className={`font-medium text-xs px-1.5 py-0.5 rounded-full ${exam.face_verification_required !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>
                                  {exam.face_verification_required !== false ? 'On' : 'Off'}
                                </span>
                              </div>
                            </div>
                            {/* Progress bar */}
                            <div className="mt-4">
                              <div className="flex justify-between text-xs text-zinc-500 mb-1">
                                <span>Completion</span><span>{progress}%</span>
                              </div>
                              <div className="w-full h-1.5 bg-zinc-100 rounded-full">
                                <div className="h-full bg-zinc-900 rounded-full transition-all" style={{ width: `${progress}%` }} />
                              </div>
                            </div>
                            <div className="flex gap-2 mt-4">
                              <Button size="sm" className="flex-1 rounded-sm h-8 bg-zinc-900 hover:bg-zinc-800 text-xs"
                                onClick={(e) => { e.stopPropagation(); setInviteCandidateExam(exam); }}
                                data-testid={`invite-btn-${exam.id}`}>
                                <UserPlus className="w-3 h-3 mr-1" />Invite
                              </Button>
                              <Button size="sm" className="flex-1 rounded-sm h-8 bg-violet-600 hover:bg-violet-500 text-white text-xs"
                                onClick={(e) => { e.stopPropagation(); setBulkImportExam(exam); }}
                                data-testid={`import-btn-${exam.id}`}>
                                <Upload className="w-3 h-3 mr-1" />Import
                              </Button>
                              <Button size="sm" variant="outline" className="rounded-sm h-8 border-indigo-200 text-indigo-600 text-xs px-2"
                                onClick={(e) => { e.stopPropagation(); setPreviewExam(exam); }}
                                data-testid={`preview-btn-${exam.id}`}
                                title="Preview Questions">
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="outline" className="rounded-sm h-8 border-zinc-200 text-xs px-2"
                                onClick={(e) => { e.stopPropagation(); downloadExamPdf(exam); }}
                                disabled={generatingPdf === exam.id}
                                data-testid={`download-btn-${exam.id}`}>
                                <Download className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Quick Stats */}
                  {analytics && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white border border-zinc-200 rounded-sm p-5">
                        <h3 className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-400 mb-4">Performance Summary</h3>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-zinc-600">Pass Rate</span>
                            <span className="text-sm font-bold text-zinc-900">{analytics.pass_rate}%</span>
                          </div>
                          <div className="w-full h-2 bg-zinc-100 rounded-full">
                            <div className="h-full bg-emerald-600 rounded-full" style={{ width: `${analytics.pass_rate}%` }} />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-zinc-600">Completed Attempts</span>
                            <span className="text-sm font-bold text-zinc-900">{analytics.completed_attempts}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-zinc-600">Flagged Candidates</span>
                            <span className={`text-sm font-bold ${analytics.flagged_candidates > 0 ? "text-red-600" : "text-zinc-900"}`}>{analytics.flagged_candidates}</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white border border-zinc-200 rounded-sm p-5">
                        <h3 className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-400 mb-4">Quick Actions</h3>
                        <div className="space-y-2">
                          {exams.slice(0, 3).map(exam => (
                            <button key={exam.id} onClick={() => selectExam(exam)} className="w-full flex items-center justify-between p-3 rounded-sm border border-zinc-100 hover:bg-zinc-50 transition-colors text-left">
                              <div>
                                <p className="text-sm font-medium text-zinc-900">{exam.name}</p>
                                <p className="text-xs text-zinc-500">{exam.completed_count}/{exam.total_candidates} completed</p>
                              </div>
                              <Eye className="w-4 h-4 text-zinc-400" />
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ===== EXAMS TAB ===== */}
              {activeTab === "exams" && (
                <div className="space-y-6">
                  {exams.length === 0 ? (
                    <div className="bg-white border border-zinc-200 rounded-sm p-16 text-center">
                      <ClipboardList className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-zinc-600" style={{ fontFamily: 'Outfit, sans-serif' }}>No exams assigned</h3>
                      <p className="text-sm text-zinc-500 mt-1">An administrator will assign exams to you.</p>
                    </div>
                  ) : (
                    exams.map(exam => {
                      const RoleIcon = getRoleIcon(exam.role);
                      return (
                        <div key={exam.id} className="bg-white border border-zinc-200 rounded-sm" data-testid={`exam-detail-${exam.id}`}>
                          <div className="p-6 border-b border-zinc-100">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-sm bg-zinc-100 flex items-center justify-center">
                                  <RoleIcon className="w-6 h-6 text-zinc-600" />
                                </div>
                                <div>
                                  <h3 className="text-xl font-semibold text-zinc-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{exam.name}</h3>
                                  <div className="flex items-center gap-3 mt-1">
                                    <Badge variant="outline" className="rounded-sm text-xs">{getRoleLabel(exam.role)}</Badge>
                                    <span className="text-xs text-zinc-500"><Clock className="w-3 h-3 inline mr-1" />{exam.duration_minutes} min</span>
                                    <span className="text-xs text-zinc-500">{exam.num_questions} questions</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" className="rounded-sm bg-zinc-900 hover:bg-zinc-800" onClick={() => setInviteCandidateExam(exam)} data-testid={`exam-invite-${exam.id}`}>
                                  <UserPlus className="w-4 h-4 mr-1.5" />Invite Candidate
                                </Button>
                                <Button size="sm" className="rounded-sm bg-violet-600 hover:bg-violet-500 text-white" onClick={() => setBulkImportExam(exam)} data-testid={`exam-bulk-import-${exam.id}`}>
                                  <Upload className="w-4 h-4 mr-1.5" />Import & Photos
                                </Button>
                                <Button size="sm" variant="outline" className="rounded-sm border-zinc-200" onClick={() => fetchCandidateVerifications(exam.id)} data-testid={`exam-candidates-${exam.id}`}>
                                  <ShieldCheck className="w-4 h-4 mr-1.5" />Candidates
                                </Button>
                                <Button size="sm" variant="outline" className="rounded-sm border-zinc-200" onClick={() => selectExam(exam)} data-testid={`exam-view-results-${exam.id}`}>
                                  <Eye className="w-4 h-4 mr-1.5" />Results
                                </Button>
                                <Button size="sm" variant="outline" className="rounded-sm border-zinc-200" onClick={() => downloadExamPdf(exam)} disabled={generatingPdf === exam.id} data-testid={`exam-download-${exam.id}`}>
                                  <Download className="w-4 h-4 mr-1.5" />{generatingPdf === exam.id ? "..." : "PDF"}
                                </Button>
                              </div>
                            </div>
                          </div>
                          {/* Assigned Candidates */}
                          {exam.assigned_emails?.length > 0 && (
                            <div className="p-6">
                              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-400 mb-3">Assigned Candidates ({exam.assigned_emails.length})</p>
                              <div className="flex flex-wrap gap-2">
                                {exam.assigned_emails.map((email, idx) => (
                                  <Badge key={idx} variant="secondary" className="rounded-sm text-xs bg-zinc-100 text-zinc-700 flex items-center gap-1.5">
                                    {email}
                                    <X className="w-3 h-3 cursor-pointer hover:text-red-600 transition-colors" onClick={() => handleRemoveCandidate(exam.id, email)} />
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* ===== RESULTS TAB ===== */}
              {activeTab === "results" && (
                <div className="space-y-6">
                  {!selectedExam ? (
                    <div className="bg-white border border-zinc-200 rounded-sm p-16 text-center">
                      <Award className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-zinc-600" style={{ fontFamily: 'Outfit, sans-serif' }}>Select an exam to view results</h3>
                      <p className="text-sm text-zinc-500 mt-1">Choose from the Overview or Examinations tab.</p>
                    </div>
                  ) : resultsLoading ? (
                    <div className="flex items-center justify-center py-24"><div className="spinner" /></div>
                  ) : (
                    <>
                      {/* Results Charts Row */}
                      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                        {/* Topic Performance */}
                        <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-sm p-5">
                          <h3 className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-400 mb-4">Topic Performance</h3>
                          {topicData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                              <BarChart data={topicData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                                <XAxis dataKey="topic" tick={{ fontSize: 11, fill: "#71717a" }} angle={-25} textAnchor="end" height={60} />
                                <YAxis tick={{ fontSize: 11, fill: "#71717a" }} domain={[0, 100]} />
                                <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #e4e4e7", fontSize: 12 }} />
                                <Bar dataKey="accuracy" name="Accuracy %" fill="#18181b" radius={[2, 2, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <p className="text-sm text-zinc-500 py-12 text-center">No topic data available yet</p>
                          )}
                        </div>
                        {/* Recommendation Distribution */}
                        <div className="bg-white border border-zinc-200 rounded-sm p-5">
                          <h3 className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-400 mb-4">Recommendations</h3>
                          {recDistribution.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                              <PieChart>
                                <Pie data={recDistribution} cx="50%" cy="50%" outerRadius={80} innerRadius={50} paddingAngle={2} dataKey="value">
                                  {recDistribution.map((entry, i) => (
                                    <Cell key={i} fill={REC_COLORS[entry.name] || "#a1a1aa"} />
                                  ))}
                                </Pie>
                                <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #e4e4e7", fontSize: 12 }} />
                                <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                              </PieChart>
                            </ResponsiveContainer>
                          ) : (
                            <p className="text-sm text-zinc-500 py-12 text-center">No data yet</p>
                          )}
                        </div>
                      </div>

                      {/* Results Table */}
                      <div className="bg-white border border-zinc-200 rounded-sm" data-testid="results-table-container">
                        <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
                          <div>
                            <h3 className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-400">Candidate Results</h3>
                            <p className="text-sm text-zinc-600 mt-1">{filteredResults.length} candidate(s)</p>
                          </div>
                          <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                            <Input placeholder="Search candidates..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                              className="pl-9 w-56 h-8 text-xs rounded-sm border-zinc-200" data-testid="search-results" />
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" data-testid="results-table">
                            <thead>
                              <tr className="bg-zinc-50 border-b border-zinc-200">
                                <th className="text-left py-2.5 px-5 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Rank</th>
                                <th className="text-left py-2.5 px-5 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Candidate</th>
                                <th className="text-left py-2.5 px-5 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Score</th>
                                <th className="text-left py-2.5 px-5 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Accuracy</th>
                                <th className="text-left py-2.5 px-5 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Time</th>
                                <th className="text-left py-2.5 px-5 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Flags</th>
                                <th className="text-left py-2.5 px-5 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Recommendation</th>
                                <th className="text-right py-2.5 px-5 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredResults.map((r) => {
                                const flags = r.proctoring_flags || {};
                                const flagCount = Object.values(flags).reduce((a, b) => a + b, 0);
                                return (
                                  <tr key={r.id} className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${flagCount > 0 ? "bg-red-50/30" : ""}`} data-testid={`result-row-${r.id}`}>
                                    <td className="py-2.5 px-5">
                                      <span className={`w-7 h-7 rounded-sm inline-flex items-center justify-center text-xs font-bold ${
                                        r.rank === 1 ? "bg-amber-100 text-amber-800" : r.rank === 2 ? "bg-zinc-200 text-zinc-800" : r.rank === 3 ? "bg-orange-100 text-orange-800" : "bg-zinc-100 text-zinc-600"
                                      }`}>{r.rank}</span>
                                    </td>
                                    <td className="py-2.5 px-5">
                                      <p className="font-medium text-zinc-900 text-sm">{r.candidate_name}</p>
                                      <p className="text-xs text-zinc-500">{r.candidate_email}</p>
                                    </td>
                                    <td className="py-2.5 px-5 font-bold text-zinc-900">{r.score}</td>
                                    <td className="py-2.5 px-5">
                                      <span className={`font-medium ${r.accuracy >= 70 ? "text-emerald-600" : r.accuracy >= 50 ? "text-amber-600" : "text-red-600"}`}>
                                        {r.accuracy?.toFixed(1)}%
                                      </span>
                                    </td>
                                    <td className="py-2.5 px-5 text-zinc-600">{Math.floor(r.time_taken_seconds / 60)}m {r.time_taken_seconds % 60}s</td>
                                    <td className="py-2.5 px-5">
                                      {flagCount > 0 ? (
                                        <Badge className="rounded-sm text-xs bg-red-50 text-red-700 border border-red-200">
                                          <AlertTriangle className="w-3 h-3 mr-1" />{flagCount}
                                        </Badge>
                                      ) : (
                                        <Badge className="rounded-sm text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
                                          <CheckCircle className="w-3 h-3 mr-1" />Clean
                                        </Badge>
                                      )}
                                    </td>
                                    <td className="py-2.5 px-5">
                                      <Badge className={`rounded-sm text-xs ${getRecBadge(r.recommendation)}`}>{r.recommendation}</Badge>
                                    </td>
                                    <td className="py-2.5 px-5 text-right">
                                      <div className="flex items-center justify-end gap-1">
                                        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm" onClick={() => viewCandidateDetail(selectedExam.id, r.id)} data-testid={`view-detail-${r.id}`}>
                                          <Eye className="w-3.5 h-3.5" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm" onClick={() => downloadCandidatePdf(r)} data-testid={`download-pdf-${r.id}`}>
                                          <Download className="w-3.5 h-3.5" />
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          {filteredResults.length === 0 && (
                            <div className="py-12 text-center text-sm text-zinc-500">No results found</div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ===== PROCTORING TAB ===== */}
              {activeTab === "proctoring" && (
                <div className="space-y-6">
                  {!selectedExam ? (
                    <div className="bg-white border border-zinc-200 rounded-sm p-16 text-center">
                      <AlertTriangle className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-zinc-600" style={{ fontFamily: 'Outfit, sans-serif' }}>Select an exam first</h3>
                      <p className="text-sm text-zinc-500 mt-1">Click an exam from Overview to view proctoring data.</p>
                    </div>
                  ) : proctoringLogsLoading ? (
                    <div className="flex items-center justify-center py-24"><div className="spinner" /></div>
                  ) : (
                    <>
                      {/* Proctoring Summary Cards */}
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                        {["tab_switch", "right_click", "copy_attempt", "fullscreen_exit"].map(type => {
                          const count = proctoringLogs.filter(l => l.event_type === type).length;
                          const uniqueCandidates = new Set(proctoringLogs.filter(l => l.event_type === type).map(l => l.candidate_email)).size;
                          return (
                            <div key={type} className={`bg-white border rounded-sm p-5 ${count > 0 ? "border-red-200 bg-red-50/30" : "border-zinc-200"}`} data-testid={`proctoring-stat-${type}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold tracking-[0.12em] uppercase text-zinc-500">{getEventLabel(type)}</span>
                                {getEventIcon(type)}
                              </div>
                              <p className={`text-3xl font-bold tracking-tight ${count > 0 ? "text-red-700" : "text-zinc-900"}`}>{count}</p>
                              <p className="text-xs text-zinc-500 mt-1">{uniqueCandidates} candidate(s)</p>
                            </div>
                          );
                        })}
                      </div>

                      {/* Proctoring Logs Table */}
                      <div className="bg-white border border-zinc-200 rounded-sm" data-testid="proctoring-logs-table">
                        <div className="p-5 border-b border-zinc-100">
                          <h3 className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-400">Malpractice Event Log</h3>
                          <p className="text-sm text-zinc-600 mt-1">{proctoringLogs.length} event(s) recorded for {selectedExam.name}</p>
                        </div>
                        {proctoringLogs.length === 0 ? (
                          <div className="py-12 text-center">
                            <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                            <p className="text-sm text-zinc-600 font-medium">No malpractice events detected</p>
                            <p className="text-xs text-zinc-500 mt-1">All candidates completed the exam without any flags.</p>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-zinc-50 border-b border-zinc-200">
                                  <th className="text-left py-2.5 px-5 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Candidate</th>
                                  <th className="text-left py-2.5 px-5 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Event Type</th>
                                  <th className="text-left py-2.5 px-5 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Details</th>
                                  <th className="text-left py-2.5 px-5 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">IP Address</th>
                                  <th className="text-left py-2.5 px-5 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Date & Time (IST)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {proctoringLogs.map((log, i) => (
                                  <tr key={i} className="border-b border-zinc-100 bg-red-50/20 hover:bg-red-50/40 transition-colors" data-testid={`proc-log-${i}`}>
                                    <td className="py-2.5 px-5">
                                      <p className="font-medium text-zinc-900 text-sm">{log.candidate_email}</p>
                                    </td>
                                    <td className="py-2.5 px-5">
                                      <Badge className="rounded-sm text-xs bg-red-50 text-red-700 border border-red-200 flex items-center gap-1.5 w-fit">
                                        {getEventIcon(log.event_type)}
                                        {getEventLabel(log.event_type)}
                                      </Badge>
                                    </td>
                                    <td className="py-2.5 px-5 text-zinc-600 text-xs">{log.details || "—"}</td>
                                    <td className="py-2.5 px-5 text-zinc-600 text-xs font-mono">{log.ip_address}</td>
                                    <td className="py-2.5 px-5 text-zinc-700 text-xs font-medium">{formatIST(log.timestamp)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Invite Candidate Dialog - Enhanced with full info + photo */}
      <Dialog open={!!inviteCandidateExam} onOpenChange={(open) => { if (!open) { setInviteCandidateExam(null); setInviteForm({ full_name: '', email: '', application_number: '', mobile: '' }); setInvitePhoto(null); } }}>
        <DialogContent className="rounded-sm border-zinc-200 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Invite Candidate to {inviteCandidateExam?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <div>
              <Label className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Full Name *</Label>
              <Input value={inviteForm.full_name} onChange={(e) => setInviteForm(p => ({...p, full_name: e.target.value}))}
                placeholder="John Doe" className="mt-1.5 rounded-sm border-zinc-200 text-sm" data-testid="invite-name-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Email *</Label>
              <Input value={inviteForm.email} onChange={(e) => setInviteForm(p => ({...p, email: e.target.value}))}
                placeholder="john@example.com" type="email" className="mt-1.5 rounded-sm border-zinc-200 text-sm" data-testid="invite-email-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Application #</Label>
                <Input value={inviteForm.application_number} onChange={(e) => setInviteForm(p => ({...p, application_number: e.target.value}))}
                  placeholder="APP001" className="mt-1.5 rounded-sm border-zinc-200 text-sm" data-testid="invite-appno-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Mobile</Label>
                <Input value={inviteForm.mobile} onChange={(e) => setInviteForm(p => ({...p, mobile: e.target.value}))}
                  placeholder="+91..." className="mt-1.5 rounded-sm border-zinc-200 text-sm" data-testid="invite-mobile-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Photo (Optional)</Label>
              <div className="mt-1.5 flex items-center gap-3">
                <label className="flex-1 flex items-center justify-center gap-2 py-3 border-2 border-dashed border-zinc-200 rounded-sm cursor-pointer hover:border-zinc-400 transition-colors" data-testid="invite-photo-upload">
                  <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => setInvitePhoto(e.target.files?.[0] || null)} />
                  <Camera className="w-4 h-4 text-zinc-400" />
                  <span className="text-xs text-zinc-500">{invitePhoto ? invitePhoto.name : "Upload candidate photo"}</span>
                </label>
                {invitePhoto && (
                  <button onClick={() => setInvitePhoto(null)} className="p-1 hover:bg-zinc-100 rounded-sm">
                    <X className="w-4 h-4 text-zinc-400" />
                  </button>
                )}
              </div>
              <p className="text-[10px] text-zinc-400 mt-1">Photo enables AI face verification on candidate&apos;s first login</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm border-zinc-200" onClick={() => setInviteCandidateExam(null)}>Cancel</Button>
            <Button onClick={handleInviteCandidate} disabled={inviteSubmitting || !inviteForm.email || !inviteForm.full_name}
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="confirm-invite-candidate-btn">
              {inviteSubmitting ? <><Activity className="w-4 h-4 mr-1.5 animate-spin" />Inviting...</> : <><UserPlus className="w-4 h-4 mr-1.5" />Invite Candidate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Candidate Verification Management Panel */}
      <Dialog open={candidateVerifications.length > 0} onOpenChange={(open) => { if (!open) setCandidateVerifications([]); }}>
        <DialogContent className="rounded-sm border-zinc-200 sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Candidate Verification Status</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {candidateVerifications.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-8">No candidates imported for this exam</p>
            ) : (
              <div className="space-y-2">
                {candidateVerifications.map(c => {
                  const statusConfig = {
                    verified: { label: "Verified", class: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle },
                    manually_verified: { label: "Manually Verified", class: "bg-blue-50 text-blue-700 border-blue-200", icon: CheckCircle },
                    pending: { label: "Pending", class: "bg-zinc-50 text-zinc-600 border-zinc-200", icon: Clock },
                    failed: { label: "Failed", class: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertTriangle },
                    blocked: { label: "Blocked (3 failures)", class: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
                  };
                  const sc = statusConfig[c.verification_status] || statusConfig.pending;
                  const StatusIcon = sc.icon;
                  return (
                    <div key={c.id} className="flex items-center justify-between p-3 bg-white border border-zinc-200 rounded-sm" data-testid={`verification-row-${c.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-sm bg-zinc-100 flex items-center justify-center flex-shrink-0">
                          <Users className="w-4 h-4 text-zinc-500" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-zinc-900 truncate">{c.full_name}</p>
                          <p className="text-xs text-zinc-500 truncate">{c.email} · {c.application_number}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge className={`rounded-sm text-xs border ${sc.class} flex items-center gap-1`}>
                          <StatusIcon className="w-3 h-3" />{sc.label}
                        </Badge>
                        {c.photo_uploaded && <Badge variant="outline" className="rounded-sm text-xs border-zinc-200"><Camera className="w-3 h-3 mr-1" />Photo</Badge>}
                        {/* Quick photo upload */}
                        <label className="cursor-pointer" data-testid={`photo-upload-btn-${c.id}`}>
                          <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
                            onChange={(e) => { if (e.target.files?.[0]) handleQuickPhotoUpload(c.id, e.target.files[0]); e.target.value = ''; }} />
                          <Badge variant="outline" className={`rounded-sm text-xs border-zinc-200 cursor-pointer hover:bg-zinc-50 ${uploadingPhotoFor === c.id ? 'opacity-50' : ''}`}>
                            <Upload className="w-3 h-3 mr-1" />{c.photo_uploaded ? 'Re-upload' : 'Upload Photo'}
                          </Badge>
                        </label>
                        {/* Edit button */}
                        <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs border-zinc-200"
                          onClick={() => openEditCandidate(c)} data-testid={`edit-candidate-btn-${c.id}`}>
                          <Pencil className="w-3 h-3 mr-1" />Edit
                        </Button>
                        {(c.verification_status === "blocked" || c.verification_status === "failed") && (
                          <Button size="sm" className="rounded-sm h-7 bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
                            disabled={verifyingCandidate === c.id}
                            onClick={() => handleManualVerify(verificationExamId, c.id)}
                            data-testid={`manual-verify-btn-${c.id}`}>
                            {verifyingCandidate === c.id ? "..." : <><ShieldCheck className="w-3 h-3 mr-1" />Override</>}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Candidate Dialog */}
      <Dialog open={!!editCandidate} onOpenChange={(open) => { if (!open) { setEditCandidate(null); setEditPhoto(null); } }}>
        <DialogContent className="rounded-sm border-zinc-200 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Edit Candidate — {editCandidate?.email}
            </DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <div>
              <Label className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Full Name</Label>
              <Input value={editForm.full_name} onChange={(e) => setEditForm(p => ({...p, full_name: e.target.value}))}
                placeholder="Full Name" className="mt-1.5 rounded-sm border-zinc-200 text-sm" data-testid="edit-candidate-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Application #</Label>
                <Input value={editForm.application_number} onChange={(e) => setEditForm(p => ({...p, application_number: e.target.value}))}
                  placeholder="APP001" className="mt-1.5 rounded-sm border-zinc-200 text-sm" data-testid="edit-candidate-appno" />
              </div>
              <div>
                <Label className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Mobile</Label>
                <Input value={editForm.mobile} onChange={(e) => setEditForm(p => ({...p, mobile: e.target.value}))}
                  placeholder="+91..." className="mt-1.5 rounded-sm border-zinc-200 text-sm" data-testid="edit-candidate-mobile" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">
                Photo {editCandidate?.photo_uploaded ? '(Re-upload to replace)' : '(Upload)'}
              </Label>
              <div className="mt-1.5 flex items-center gap-3">
                <label className="flex-1 flex items-center justify-center gap-2 py-3 border-2 border-dashed border-zinc-200 rounded-sm cursor-pointer hover:border-zinc-400 transition-colors" data-testid="edit-candidate-photo-upload">
                  <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => setEditPhoto(e.target.files?.[0] || null)} />
                  <Camera className="w-4 h-4 text-zinc-400" />
                  <span className="text-xs text-zinc-500">{editPhoto ? editPhoto.name : editCandidate?.photo_uploaded ? 'Choose new photo to replace' : 'Upload candidate photo'}</span>
                </label>
                {editPhoto && (
                  <button onClick={() => setEditPhoto(null)} className="p-1 hover:bg-zinc-100 rounded-sm" data-testid="edit-remove-photo">
                    <X className="w-4 h-4 text-zinc-400" />
                  </button>
                )}
              </div>
              {editCandidate?.photo_uploaded && !editPhoto && (
                <p className="text-[10px] text-emerald-600 mt-1">Current photo exists. Upload a new one to replace it.</p>
              )}
              {editPhoto && (
                <p className="text-[10px] text-amber-600 mt-1">New photo will replace the existing one and reset verification to pending.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm border-zinc-200" onClick={() => setEditCandidate(null)}>Cancel</Button>
            <Button onClick={handleUpdateCandidate} disabled={editSubmitting}
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="save-edit-candidate-btn">
              {editSubmitting ? <><Activity className="w-4 h-4 mr-1.5 animate-spin" />Saving...</> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Candidate Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-sm border-zinc-200">
          <DialogHeader>
            <DialogTitle className="tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Candidate Detail Report</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-16"><div className="spinner" /></div>
          ) : selectedCandidate && (
            <div className="space-y-6 py-2">
              {/* Header */}
              <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-sm border border-zinc-200">
                <div>
                  <h3 className="text-lg font-bold text-zinc-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedCandidate.candidate_name}</h3>
                  <p className="text-sm text-zinc-500">{selectedCandidate.candidate_email}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={`rounded-sm px-3 py-1 text-sm ${getRecBadge(selectedCandidate.recommendation)}`}>{selectedCandidate.recommendation}</Badge>
                  <span className={`w-10 h-10 rounded-sm inline-flex items-center justify-center text-sm font-bold ${
                    selectedCandidate.rank === 1 ? "bg-amber-100 text-amber-800" : selectedCandidate.rank === 2 ? "bg-zinc-200 text-zinc-800" : selectedCandidate.rank === 3 ? "bg-orange-100 text-orange-800" : "bg-zinc-100 text-zinc-600"
                  }`}>#{selectedCandidate.rank}</span>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: "Score", value: selectedCandidate.score, color: "text-zinc-900" },
                  { label: "Accuracy", value: `${selectedCandidate.accuracy?.toFixed(1)}%`, color: selectedCandidate.accuracy >= 70 ? "text-emerald-600" : "text-amber-600" },
                  { label: "Time", value: `${Math.floor(selectedCandidate.time_taken_seconds / 60)}m ${selectedCandidate.time_taken_seconds % 60}s`, color: "text-zinc-900" },
                  { label: "Correct", value: `${selectedCandidate.correct_answers}/${selectedCandidate.total_questions}`, color: "text-zinc-900" },
                ].map((s, i) => (
                  <div key={i} className="bg-white border border-zinc-200 rounded-sm p-3 text-center">
                    <p className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-400">{s.label}</p>
                    <p className={`text-xl font-bold mt-1 ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Difficulty Breakdown */}
              {selectedCandidate.difficulty_breakdown && (
                <div>
                  <h4 className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-400 mb-3">Difficulty Breakdown</h4>
                  <div className="grid grid-cols-3 gap-3">
                    {["easy", "medium", "hard"].map(diff => {
                      const d = selectedCandidate.difficulty_breakdown[diff] || {};
                      const pct = d.total > 0 ? Math.round((d.correct / d.total) * 100) : 0;
                      return (
                        <div key={diff} className="bg-white border border-zinc-200 rounded-sm p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 capitalize">{diff}</p>
                            <span className="text-xs text-zinc-400">{d.points || 0} pts</span>
                          </div>
                          <p className="text-2xl font-bold text-zinc-900">{d.correct || 0}/{d.total || 0}</p>
                          <div className="w-full h-1.5 bg-zinc-100 rounded-full mt-2">
                            <div className={`h-full rounded-full ${pct >= 70 ? "bg-emerald-600" : pct >= 40 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Proctoring Log */}
              <div>
                <h4 className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-400 mb-3">
                  Proctoring Evidence Log
                  {candidateProcLogs.length > 0 && <Badge className="ml-2 rounded-sm text-xs bg-red-50 text-red-700 border border-red-200">{candidateProcLogs.length} event(s)</Badge>}
                </h4>
                {candidateProcLogs.length === 0 ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-emerald-800">No malpractice detected</p>
                      <p className="text-xs text-emerald-600">This candidate completed the exam without any proctoring violations.</p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white border border-red-200 rounded-sm overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-red-50/50 border-b border-red-200">
                          <th className="text-left py-2 px-4 text-xs font-semibold text-red-700">Event</th>
                          <th className="text-left py-2 px-4 text-xs font-semibold text-red-700">Details</th>
                          <th className="text-left py-2 px-4 text-xs font-semibold text-red-700">IP Address</th>
                          <th className="text-left py-2 px-4 text-xs font-semibold text-red-700">Timestamp (IST)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {candidateProcLogs.map((log, i) => (
                          <tr key={i} className="border-b border-red-100 hover:bg-red-50/30">
                            <td className="py-2 px-4">
                              <div className="flex items-center gap-2">
                                {getEventIcon(log.event_type)}
                                <span className="text-xs font-medium text-red-700">{getEventLabel(log.event_type)}</span>
                              </div>
                            </td>
                            <td className="py-2 px-4 text-xs text-zinc-600">{log.details || "—"}</td>
                            <td className="py-2 px-4 text-xs text-zinc-600 font-mono">{log.ip_address}</td>
                            <td className="py-2 px-4 text-xs text-zinc-700 font-medium">{formatIST(log.timestamp)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Import Modal */}
      {bulkImportExam && (
        <BulkCandidateImport
          examId={bulkImportExam.id}
          examName={bulkImportExam.name}
          onClose={() => { setBulkImportExam(null); fetchData(); }}
        />
      )}

      {/* Question Preview Dialog */}
      <QuestionPreviewDialog
        open={!!previewExam}
        onClose={() => setPreviewExam(null)}
        examId={previewExam?.id}
        examName={previewExam?.name}
      />
    </div>
  );
};

export default OrganiserDashboard;
